/**
 * STORY-035: Gemini Flash Integration
 * Service for Google Gemini API
 */

import type {
  GeminiConfig,
  GeminiRequest,
  GeminiResponse,
  AIMessage,
  ProjectType,
  BMADResponse,
} from '@/types/ai'

// Default configuration
const DEFAULT_CONFIG: GeminiConfig = {
  apiKey: import.meta.env.VITE_GEMINI_API_KEY || '',
  model: 'gemini-1.5-flash',
  maxTokens: 2048,
  temperature: 0.7,
}

// System instructions loaded from database (ai_instructions table)
// Prompts are stored server-side and loaded at runtime to prevent exposure in JS bundles
import { supabase } from '@/lib/supabase'

const instructionCache: Record<string, string> = {}

async function getSystemInstruction(name: string): Promise<string> {
  if (instructionCache[name]) return instructionCache[name]

  const { data } = await supabase
    .from('ai_instructions')
    .select('prompt_template')
    .eq('name', name)
    .eq('is_active', true)
    .single()

  const prompt = data?.prompt_template || ''
  if (prompt) instructionCache[name] = prompt
  return prompt
}

// Mapping for backward compatibility
const INSTRUCTION_KEYS: Record<string, string> = {
  questionnaire: 'questionnaire_system',
  summary: 'summary_system',
  conversation: 'conversation_system',
  intervention: 'intervention_check_system',
}

// Sanitize user input before sending to AI
function sanitizeAIInput(input: string): string {
  return input
    .replace(/<[^>]*>/g, '') // Strip HTML tags
    .replace(/javascript\s*:/gi, '') // Remove javascript: URLs
    .substring(0, 5000) // Limit length
}

// Gemini API endpoint
const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models'

/**
 * Build the API URL for Gemini
 */
function buildApiUrl(config: GeminiConfig): string {
  return `${GEMINI_API_URL}/${config.model}:generateContent?key=${config.apiKey}`
}

/**
 * Format conversation history for Gemini
 */
function formatConversationHistory(messages: AIMessage[]): Array<{ role: string; parts: Array<{ text: string }> }> {
  return messages
    .filter((msg) => msg.role !== 'system')
    .map((msg) => ({
      role: msg.role === 'assistant' ? 'model' : 'user',
      parts: [{ text: msg.content }],
    }))
}

/**
 * Main function to generate AI response
 */
export async function generateResponse(
  request: GeminiRequest,
  config: Partial<GeminiConfig> = {}
): Promise<GeminiResponse> {
  const finalConfig = { ...DEFAULT_CONFIG, ...config }

  if (!finalConfig.apiKey) {
    throw new Error('Gemini API key is not configured')
  }

  const url = buildApiUrl(finalConfig)

  // Build request body
  const contents: Array<{ role: string; parts: Array<{ text: string }> }> = []

  // Add conversation history if present
  if (request.conversationHistory && request.conversationHistory.length > 0) {
    contents.push(...formatConversationHistory(request.conversationHistory))
  }

  // Add current prompt (sanitized)
  contents.push({
    role: 'user',
    parts: [{ text: sanitizeAIInput(request.prompt) }],
  })

  const body = {
    contents,
    systemInstruction: {
      parts: [{ text: request.systemInstruction || await getSystemInstruction(INSTRUCTION_KEYS.conversation) }],
    },
    generationConfig: {
      temperature: finalConfig.temperature,
      maxOutputTokens: finalConfig.maxTokens,
      topP: 0.95,
      topK: 40,
    },
    safetySettings: [
      { category: 'HARM_CATEGORY_HARASSMENT', threshold: 'BLOCK_MEDIUM_AND_ABOVE' },
      { category: 'HARM_CATEGORY_HATE_SPEECH', threshold: 'BLOCK_MEDIUM_AND_ABOVE' },
      { category: 'HARM_CATEGORY_SEXUALLY_EXPLICIT', threshold: 'BLOCK_MEDIUM_AND_ABOVE' },
      { category: 'HARM_CATEGORY_DANGEROUS_CONTENT', threshold: 'BLOCK_MEDIUM_AND_ABOVE' },
    ],
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        `Gemini API error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`
      )
    }

    const data = await response.json()

    // Extract response
    const candidate = data.candidates?.[0]
    if (!candidate) {
      throw new Error('No response generated by Gemini')
    }

    const text = candidate.content?.parts?.[0]?.text || ''

    return {
      text,
      tokens: {
        input: data.usageMetadata?.promptTokenCount || 0,
        output: data.usageMetadata?.candidatesTokenCount || 0,
        total: data.usageMetadata?.totalTokenCount || 0,
      },
      finishReason: candidate.finishReason || 'STOP',
      safetyRatings: candidate.safetyRatings,
    }
  } catch (error) {
    console.error('Gemini API error:', error)
    throw error
  }
}

/**
 * Generate a questionnaire response based on project type
 */
export async function generateQuestionnaireResponse(
  projectType: ProjectType,
  previousAnswers: Record<string, string | string[]>,
  currentQuestion: string,
  userResponse: string
): Promise<GeminiResponse> {
  const context = `
Type de projet: ${projectType}
Reponses precedentes: ${JSON.stringify(previousAnswers, null, 2)}
Question actuelle: ${currentQuestion}
`

  return generateResponse({
    prompt: userResponse,
    context,
    systemInstruction: await getSystemInstruction(INSTRUCTION_KEYS.questionnaire),
  })
}

/**
 * Analyze client responses and generate BMAD summary
 */
export async function analyzeClientResponses(
  projectType: ProjectType,
  bmadResponses: Partial<BMADResponse>,
  conversationHistory: AIMessage[]
): Promise<{
  technicalSummary: string
  clientNeeds: string[]
  suggestedSolutions: string[]
  bmadAnalysis: {
    background: string
    motivation: string
    aspirations: string
    decisionMaking: string
  }
  suggestedPrompts: string[]
  confidenceScore: number
}> {
  const prompt = `
Analyse les reponses suivantes du client pour un projet de type "${projectType}" et genere un resume structure.

Reponses BMAD collectees:
${JSON.stringify(bmadResponses, null, 2)}

Historique de la conversation:
${conversationHistory.map((m) => `${m.role}: ${m.content}`).join('\n')}

Genere un JSON avec la structure suivante:
{
  "technicalSummary": "Resume technique du projet en 2-3 phrases",
  "clientNeeds": ["besoin 1", "besoin 2", ...],
  "suggestedSolutions": ["solution 1", "solution 2", ...],
  "bmadAnalysis": {
    "background": "Analyse du contexte client",
    "motivation": "Analyse des motivations",
    "aspirations": "Analyse des objectifs",
    "decisionMaking": "Analyse du processus de decision"
  },
  "suggestedPrompts": ["question de suivi 1", "question de suivi 2", ...],
  "confidenceScore": 0.85
}

Reponds UNIQUEMENT avec le JSON, sans texte supplementaire.
`

  const response = await generateResponse({
    prompt,
    systemInstruction: await getSystemInstruction(INSTRUCTION_KEYS.summary),
  })

  try {
    // Extract JSON from response
    const jsonMatch = response.text.match(/\{[\s\S]*\}/)
    if (!jsonMatch) {
      throw new Error('No JSON found in response')
    }
    return JSON.parse(jsonMatch[0])
  } catch {
    // Return default structure if parsing fails
    return {
      technicalSummary: 'Analyse en cours...',
      clientNeeds: [],
      suggestedSolutions: [],
      bmadAnalysis: {
        background: '',
        motivation: '',
        aspirations: '',
        decisionMaking: '',
      },
      suggestedPrompts: [],
      confidenceScore: 0,
    }
  }
}

/**
 * Check if the AI should request human intervention
 */
export async function shouldRequestIntervention(
  userMessage: string,
  conversationHistory: AIMessage[]
): Promise<{
  shouldIntervene: boolean
  reason?: string
  priority?: 'low' | 'medium' | 'high' | 'urgent'
}> {
  const prompt = `
Analyse le message suivant et determine si une intervention humaine est necessaire.

Message: "${userMessage}"

Historique recent:
${conversationHistory.slice(-5).map((m) => `${m.role}: ${m.content}`).join('\n')}

Reponds avec un JSON:
{
  "shouldIntervene": true/false,
  "reason": "raison si intervention necessaire",
  "priority": "low/medium/high/urgent"
}

Raisons d'intervention:
- Question technique tres complexe
- Negociation de prix
- Reclamation ou insatisfaction
- Demande urgente
- Sujet hors contexte
- Demande explicite d'un humain
- Information sensible

Reponds UNIQUEMENT avec le JSON.
`

  const response = await generateResponse({
    prompt,
    systemInstruction: await getSystemInstruction(INSTRUCTION_KEYS.intervention),
  })

  try {
    const jsonMatch = response.text.match(/\{[\s\S]*\}/)
    if (!jsonMatch) {
      return { shouldIntervene: false }
    }
    return JSON.parse(jsonMatch[0])
  } catch {
    return { shouldIntervene: false }
  }
}

/**
 * Generate a conversational response
 */
export async function generateConversationalResponse(
  userMessage: string,
  conversationHistory: AIMessage[],
  context?: {
    projectType?: ProjectType
    clientName?: string
  }
): Promise<GeminiResponse> {
  let systemInstruction = await getSystemInstruction(INSTRUCTION_KEYS.conversation)

  if (context?.clientName) {
    systemInstruction += `\nLe client s'appelle ${context.clientName}.`
  }

  if (context?.projectType) {
    systemInstruction += `\nLe projet concerne: ${context.projectType}.`
  }

  return generateResponse({
    prompt: userMessage,
    conversationHistory,
    systemInstruction,
  })
}

// Export system instructions for reuse
export { getSystemInstruction, INSTRUCTION_KEYS }
